import type { Invitation } from '@/api/hooks/useInvitationPreview';

export type InvitationScopeStatus = Invitation['scope'] extends string
  ? 'pending' | 'accepted' | 'expired' | 'revoked' | 'declined' | 'mismatched'
  : never;

export type InvitationScopeCardProps = {
  invitation: Invitation;
  status: InvitationScopeStatus;
  currentEmailMasked?: string;
  onAccept: () => void;
  onDecline: () => void;
  onReport: () => void;
  onSwitchAccount?: () => void;
  onRequestNew?: () => void;
};

function expiresLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return 'an unknown date';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
}

export function InvitationScopeCard({
  invitation,
  status,
  currentEmailMasked,
  onAccept,
  onDecline,
  onReport,
  onSwitchAccount,
  onRequestNew,
}: InvitationScopeCardProps) {
  const terminal = status === 'expired' || status === 'revoked';
  const ownerFirstName = invitation.owner_display_name.split(' ')[0];
  return (
    <section className="w-full max-w-xl space-y-5 rounded-xl border bg-card p-6 shadow-sm sm:p-8" aria-labelledby="invitation-scope-heading">
      <div>
        <p className="font-mono text-xs font-semibold text-muted-foreground">MoneySkyline</p>
        <h1 id="invitation-scope-heading" tabIndex={-1} className="mt-4 text-2xl font-semibold tracking-tight focus:outline-none">
          Invitation to review {invitation.owner_display_name}’s {invitation.tax_year} business books
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">Role: accountant · Scope: business rows only · Accounts shared: {invitation.shared_accounts.length} · Expires {expiresLabel(invitation.expires_at)}</p>
      </div>

      <div className="space-y-2 rounded-lg border bg-muted/30 p-4 text-sm">
        <p className="font-semibold">What you get</p>
        <p>Business income and expenses only.</p>
        <p>{invitation.shared_accounts.length} shared account{invitation.shared_accounts.length === 1 ? '' : 's'}.</p>
        <p>You can recategorize, add notes and ask {ownerFirstName} questions. Every change is logged and reversible.</p>
        <p className="text-muted-foreground">You will not see personal transactions or bank connections.</p>
      </div>

      {status === 'mismatched' && (
        <div role="alert" className="rounded-lg border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">
          <p>This invitation was sent to {invitation.invitee_email_masked}.</p>
          <p>You are signed in as {currentEmailMasked ?? 'another account'}. Switch to the invited account to accept, or ask {ownerFirstName} to resend to this address.</p>
        </div>
      )}

      {status === 'declined' && <p role="status" className="rounded-lg border p-4 text-sm">This invitation was declined.</p>}
      {terminal && (
        <div role="alert" className="space-y-3 rounded-lg border border-degraded-border bg-degraded-surface p-4 text-sm">
          <p>{status === 'expired' ? 'This invitation has expired. Ask the owner to resend it.' : 'This invitation was revoked. Ask the owner to resend it.'}</p>
          <button type="button" onClick={onRequestNew} className="min-h-11 w-full rounded-md border border-input px-4 font-semibold">Request a new invitation</button>
        </div>
      )}

      {status === 'accepted' && <p role="status" className="rounded-lg border p-4 text-sm">This invitation was already accepted.</p>}

      {(status === 'pending' || status === 'mismatched') && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Accepting links this invitation to the account shown. {invitation.owner_display_name} can revoke access at any time. Records only. Not tax advice.</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={onAccept} disabled={status === 'mismatched'} className="min-h-12 flex-1 rounded-md bg-primary px-4 font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50">Accept invitation</button>
            {status === 'mismatched' && <button type="button" onClick={onSwitchAccount} className="min-h-12 flex-1 rounded-md border border-input px-4 font-semibold">Switch account</button>}
            <button type="button" onClick={onDecline} className="min-h-12 rounded-md border border-input px-4 font-semibold">Decline</button>
          </div>
          <button type="button" onClick={onReport} className="min-h-11 w-full text-sm text-muted-foreground underline underline-offset-4">Report this invitation</button>
        </div>
      )}
    </section>
  );
}
